import React, { useState } from 'react'
import { Card, Button, Space, message, Divider, Typography } from 'antd'
import {
  createLedgerTransaction,
  getPatientLedgerTransactions,
  recordRemittancePayment,
  getPatientLedgerSummary
} from '../../backend-integration/api'

const { Title, Text } = Typography

/**
 * Test component to verify billing integration
 * This component helps debug why transactions aren't showing in patient ledger
 */
const TestBillingIntegration = ({ patientId }) => {
  const [loading, setLoading] = useState(false)
  const [transactions, setTransactions] = useState([])
  const [summary, setSummary] = useState(null)

  // Test creating a manual ledger entry
  const testCreateLedgerEntry = async () => {
    try {
      setLoading(true)

      const testTransaction = {
        patientId: patientId,
        date: new Date(),
        type: 'medical_aid_payment',
        description: 'TEST - Medical Aid Payment',
        invoiceNo: `TEST-${Date.now()}`,
        debit: 0,
        credit: 100,
        status: 'Processed',
        medicalAidAmount: 100,
        patientAmount: 0,
        notes: 'Test transaction to verify integration'
      }

      console.log('Creating test transaction:', testTransaction)
      const result = await createLedgerTransaction(testTransaction)
      console.log('Transaction created:', result)

      message.success('Test transaction created successfully!')

      // Refresh the transactions
      await loadTransactions()
    } catch (error) {
      console.error('Error creating test transaction:', error)
      message.error(`Failed to create test transaction: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Test medical aid payment
  const testMedicalAidPayment = async () => {
    try {
      setLoading(true)

      const paymentData = {
        patientId: patientId,
        invoiceNo: `INV-TEST-${Date.now()}`,
        amount: 250,
        medicalAid: 'Test Medical Aid',
        receiptNumber: `REC-TEST-${Date.now()}`,
        remittanceDate: new Date(),
        remittanceId: `REM-TEST-${Date.now()}`
      }

      console.log('Creating medical aid payment:', paymentData)
      const result = await recordRemittancePayment(paymentData)
      console.log('Payment created:', result)

      message.success('Medical aid payment created successfully!')

      // Refresh the transactions
      await loadTransactions()
    } catch (error) {
      console.error('Error creating medical aid payment:', error)
      message.error(`Failed to create medical aid payment: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Load transactions
  const loadTransactions = async () => {
    try {
      setLoading(true)

      const filter = {
        patientId: patientId
      }

      console.log('Loading transactions for patient:', patientId)
      const txns = await getPatientLedgerTransactions(filter, 1, 50)
      console.log('Transactions loaded:', txns)

      setTransactions(Array.isArray(txns) ? txns : [])

      // Also load summary
      const summaryData = await getPatientLedgerSummary(patientId)
      console.log('Summary loaded:', summaryData)
      setSummary(summaryData)

      message.success(`Loaded ${txns.length} transactions`)
    } catch (error) {
      console.error('Error loading transactions:', error)
      message.error(`Failed to load transactions: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card title='Billing Integration Test' bordered={false}>
      <Space direction='vertical' style={{ width: '100%' }}>
        <Title level={4}>Patient ID: {patientId || 'Not Set'}</Title>
        <Text>Account No: {null || 'Not Set'}</Text>

        <Divider />

        <Space>
          <Button type='primary' onClick={testCreateLedgerEntry} loading={loading}>
            Test Create Ledger Entry
          </Button>

          <Button type='primary' onClick={testMedicalAidPayment} loading={loading}>
            Test Medical Aid Payment
          </Button>

          <Button onClick={loadTransactions} loading={loading}>
            Load Transactions
          </Button>
        </Space>

        <Divider />

        {summary && (
          <Card size='small' title='Account Summary'>
            <Space direction='vertical'>
              <Text>Total Debits: R{summary.totalDebits?.toFixed(2) || '0.00'}</Text>
              <Text>Total Credits: R{summary.totalCredits?.toFixed(2) || '0.00'}</Text>
              <Text>Current Balance: R{summary.currentBalance?.toFixed(2) || '0.00'}</Text>
              <Text>Transaction Count: {summary.transactionCount || 0}</Text>
            </Space>
          </Card>
        )}

        <Divider />

        <Card size='small' title={`Transactions (${transactions.length})`}>
          {transactions.length === 0 ? (
            <Text>No transactions found. Check console for details.</Text>
          ) : (
            <div style={{ maxHeight: '300px', overflow: 'auto' }}>
              {transactions.map((txn, index) => (
                <div key={index} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #f0f0f0' }}>
                  <Space direction='vertical' size='small'>
                    <Text strong>{txn.description}</Text>
                    <Text>Type: {txn.type}</Text>
                    <Text>
                      Debit: R{txn.debit || 0} | Credit: R{txn.credit || 0}
                    </Text>
                    <Text>Date: {new Date(txn.date).toLocaleDateString()}</Text>
                    {txn.invoiceNo && <Text>Invoice: {txn.invoiceNo}</Text>}
                  </Space>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Divider />

        <Text type='secondary'>Check the browser console (F12) for detailed logs of what's happening.</Text>
      </Space>
    </Card>
  )
}

export default TestBillingIntegration
